import type { SVGProps } from 'react';

/**
 * VRG-789 — the four social-network marks, owned locally instead of imported.
 *
 * ## Why these are not `lucide-react` imports any more
 *
 * They used to be, and it broke the whole app. `apps/app` declares
 * `lucide-react@^0.544.0`, which HAS brand icons; `packages/ui` declares
 * `^1.47.0`, which does NOT — upstream removed every brand mark in 1.x. Because
 * `apps/app` consumes `@repo/ui` as SOURCE (working-conventions §47), both
 * import graphs are optimized together and Vite emits ONE `deps/lucide-react.js`.
 * It resolved 1.47, so `import { Facebook } from 'lucide-react'` had no export,
 * module evaluation threw, and the shadow root never attached: every route blank.
 *
 * `tsc` could not see it. The type resolver picks `apps/app`'s own 0.544, whose
 * `.d.ts` declares `Facebook`, so the app typechecked clean at its full baseline
 * while being completely dead in a browser. The disagreement existed only
 * between the resolver and the bundler.
 *
 * An audit of every lucide identifier `apps/app` imports found exactly FOUR that
 * 1.47 lacks — these four, all brand marks. Nothing else. So owning them here
 * leaves the app importing only identifiers present in BOTH versions, and it no
 * longer matters which copy the bundler picks. That is the point: this is not a
 * patch over four icons, it makes the app version-agnostic, which is the only
 * property that survives the next icon someone adds.
 *
 * ## Provenance
 *
 * Geometry copied verbatim from `lucide-react@0.544.0`, which is **ISC**
 * licensed (see the header of each `dist/esm/icons/*.js`). These are the same
 * marks the feature already shipped with, so this preserves the design rather
 * than changing it — deliberately, because which marks represent a network is a
 * design call and this change is a bug fix.
 *
 * ## What is NOT fixed here
 *
 * The monorepo still carries two majors of one package, so the underlying class
 * is alive: the next 1.47-only icon used here, or 0.544-only icon in
 * `packages/ui`, breaks the same way and just as silently. Converging on one
 * version — and a guard that fails when a workspace's bundler-resolved major
 * differs from its declared pin — stays open on
 * VRG-789-App.Lucide.Brand.Icons.Break.Mount.
 *
 * Props match lucide's call shape (`size`, `strokeWidth`) so call sites read the
 * same as the surrounding generic icons.
 */

type BrandIconProps = Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> & {
  size?: number | string;
  strokeWidth?: number | string;
};

/** lucide's own wrapper attributes, so these sit on the baseline with its icons. */
const base = (size: number | string, strokeWidth: number | string) => ({
  xmlns: 'http://www.w3.org/2000/svg',
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

export const FacebookIcon = ({ size = 24, strokeWidth = 2, ...rest }: BrandIconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

export const InstagramIcon = ({ size = 24, strokeWidth = 2, ...rest }: BrandIconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

export const LinkedinIcon = ({ size = 24, strokeWidth = 2, ...rest }: BrandIconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect width="4" height="12" x="2" y="9" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

export const YoutubeIcon = ({ size = 24, strokeWidth = 2, ...rest }: BrandIconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
    <path d="m10 15 5-3-5-3z" />
  </svg>
);
